import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  DatePicker,
  EmptyState,
  FormModal,
  Input,
  Page,
  ResponsiveGrid,
  Select,
  Table,
  Text,
  Textarea,
  type Column,
} from "@marekl/adminer/components";
import { useConfirm, useTableSort } from "@marekl/adminer/hooks";
import { useSearchParams } from "react-router-dom";
import { useRuleSets } from "../../../lib/queries/rules";
import { AppTopbar } from "../../../shell/AppTopbar";
import { parseApiDate, toApiDate, type Dayjs } from "@marekl/adminer/datetime";
import { formatDate } from "@marekl/adminer/format";
import {
  useAdminSeasons,
  useCreateSeason,
  useUpdateSeason,
  useActivateSeason,
  type SeasonAdmin,
  type SeasonWritePayload,
} from "../../../lib/queries/seasons";
import { SEASON_PHASE_LABELS, SEASON_PHASE_VARIANT } from "../../../lib/seasonPhase";
import { readsOnlyAsGlobalViewer, useMe } from "../../../auth/queries";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; season: SeasonAdmin };

interface FormState {
  code: string;
  /** "" is „nevybráno"; otherwise a RuleSet id as a string. */
  default_rule_set_id: string;
  registration_open_from: Dayjs | null;
  registration_open_to: Dayjs | null;
  active: boolean;
  config: string;
}

function emptyForm(): FormState {
  return {
    code: "",
    default_rule_set_id: "",
    registration_open_from: null,
    registration_open_to: null,
    active: false,
    config: "",
  };
}

function formFromSeason(s: SeasonAdmin): FormState {
  return {
    code: s.code,
    default_rule_set_id: s.default_rule_set_id ? String(s.default_rule_set_id) : "",
    registration_open_from: s.registration_open_from
      ? parseApiDate(s.registration_open_from)
      : null,
    registration_open_to: s.registration_open_to
      ? parseApiDate(s.registration_open_to)
      : null,
    active: s.active,
    config: s.config ?? "",
  };
}

function formToPayload(f: FormState, includeCode: boolean): SeasonWritePayload {
  const payload: SeasonWritePayload = {
    // Empty string clears the bound; both-or-neither is enforced server-side.
    registration_open_from: toApiDate(f.registration_open_from) ?? "",
    registration_open_to: toApiDate(f.registration_open_to) ?? "",
    // 0 clears it. Always sent, so „žádná" is a choice the form can make
    // rather than a value it is unable to express.
    default_rule_set_id: f.default_rule_set_id || "",
    config: f.config.trim(),
  };
  if (includeCode) {
    payload.code = f.code.trim();
    payload.active = f.active;
  }
  return payload;
}

export function SeasonsPage() {
  const { data: me } = useMe();
  // Čtení všeho reads the seasons and their documents and changes neither.
  const readOnly = readsOnlyAsGlobalViewer(me, "members_admin");
  const { data: seasons = [], isLoading } = useAdminSeasons();
  const create = useCreateSeason();
  // Destructured: mutateAsync is a stable reference, so the memoised columns
  // do not re-sort when the mutation changes state.
  const { mutateAsync: activateSeason } = useActivateSeason();
  const confirm = useConfirm();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState<FormState>(emptyForm());

  // Every set, with the ones labelled for the season being edited first.
  // Ordering only — the label cannot decide, which is the whole reason this
  // field exists.
  const { data: ruleSets = [] } = useRuleSets();
  const editingSeasonId = modal.kind === "edit" ? modal.season.id : null;
  const ruleSetOptions = useMemo(() => {
    const mine = ruleSets.filter((r) => r.season_id === editingSeasonId);
    const rest = ruleSets.filter((r) => r.season_id !== editingSeasonId);
    return [...mine, ...rest];
  }, [ruleSets, editingSeasonId]);

  const editId = modal.kind === "edit" ? modal.season.id : "";
  const update = useUpdateSeason(editId);
  // Destructured because useMutation returns a NEW object every render
  // ({ ...result, mutate, mutateAsync }), so depending on the mutation
  // itself would rebuild the callback — and with it the memoised columns —
  // on every render, which is exactly what the memo is for. `reset` is
  // bound once in the observer's constructor, so it IS stable.
  const { reset: resetUpdate } = update;

  const isWriting = create.isPending || update.isPending;
  const writeError = create.error || update.error;

  const close = () => {
    setModal({ kind: "closed" });
    setForm(emptyForm());
    create.reset();
    update.reset();
  };

  const openCreate = () => {
    setForm(emptyForm());
    create.reset();
    setModal({ kind: "create" });
  };

  // useCallback because the columns are memoised for useTableSort.
  const openEdit = useCallback(
    (s: SeasonAdmin) => {
      setForm(formFromSeason(s));
      resetUpdate();
      setModal({ kind: "edit", season: s });
    },
    [resetUpdate],
  );

  const submit = () => {
    if (modal.kind === "create") {
      create.mutate(formToPayload(form, true), { onSuccess: close });
    } else if (modal.kind === "edit") {
      update.mutate(formToPayload(form, false), { onSuccess: close });
    }
  };

  // The registration window must be set as a pair (or left empty).
  const windowIncomplete =
    !!form.registration_open_from !== !!form.registration_open_to;

  const formInvalid = useMemo(() => {
    if (modal.kind === "create" && !form.code.trim()) return true;
    if (windowIncomplete) return true;
    return false;
  }, [form, modal.kind, windowIncomplete]);

  // Hoisted for useTableSort, which re-sorts on the columns identity.
  const columns = useMemo<Column<SeasonAdmin>[]>(
    () => [
            { key: "code", header: "Kód", sortable: true, sortValue: (s) => s.code, render: (s) => s.code },
            {
              key: "registration",
              header: "Registrace",
              sortable: true,
              // The phase the badge names, not the window beneath it — the
              // column's subject is where registration stands.
              sortValue: (s) => SEASON_PHASE_LABELS[s.phase],
              render: (s) => (
                <>
                  <Badge variant={SEASON_PHASE_VARIANT[s.phase]}>
                    {SEASON_PHASE_LABELS[s.phase]}
                  </Badge>
                  <div>
                    <Text variant="muted">
                      {s.registration_open_from && s.registration_open_to
                        ? `${formatDate(parseApiDate(s.registration_open_from))}–${formatDate(parseApiDate(s.registration_open_to))}`
                        : "bez omezení"}
                    </Text>
                  </div>
                </>
              ),
            },
            {
              key: "active",
              header: "Stav",
              sortable: true,
              // Booleans sort false-first ascending, so descending brings the
              // one active season to the top — the click people want.
              sortValue: (s) => s.active,
              render: (s) => (s.active ? <Badge variant="ok">Aktivní</Badge> : <Badge variant="muted">—</Badge>),
            },
            {
              key: "actions",
              header: "",
              render: (s) => (
                <Table.Actions>
                  {!readOnly && (
                    <Table.Action appearance="button" onClick={() => openEdit(s)}>Upravit</Table.Action>
                  )}
                  <Table.Action appearance="button" to={`/admin/sezony/${s.id}/dokumenty`}>
                    Dokumenty
                  </Table.Action>
                  {!readOnly && !s.active && (
                    <Table.Action appearance="button" onClick={() =>
                        confirm({
                          title: "Aktivovat sezónu",
                          description: (
                            <>
                              Sezóna <strong>{s.code}</strong> se stane aktivní. Dosavadní
                              aktivní sezóna bude deaktivována. Pokračovat?
                            </>
                          ),
                          confirmLabel: "Aktivovat",
                          onConfirm: async () => {
                            await activateSeason(s.id);
                          },
                        })
                      }>
                      Aktivovat
                    </Table.Action>
                  )}
                </Table.Actions>
              ),
            },
    ],
    [openEdit, confirm, activateSeason, readOnly],
  );

  const tableSort = useTableSort(seasons, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "code", dir: "desc" },
  });

  return (
    <>
      <AppTopbar crumbs={[{ label: "Sezóny" }]} />
      <Page>
        <Page.Header
          title="Sezóny"
          subtitle="správa soutěžních sezón a jejich nastavení"
          actions={!readOnly && <Button variant="primary" onClick={openCreate}>Nová sezóna</Button>}
        />
        <Page.Content stack gap="lg">
          <Table<SeasonAdmin>
            framed
            {...tableSort.tableProps}
            rowKey={(s) => s.id}
            columns={columns}
            isLoading={isLoading}
            empty={
              <EmptyState title="Žádné sezóny" description="Zatím nebyla založena žádná sezóna." />
            }
          />
        </Page.Content>
      </Page>

      <FormModal
        open={modal.kind === "create" || modal.kind === "edit"}
        title={modal.kind === "edit" ? "Upravit sezónu" : "Nová sezóna"}
        submitLabel={modal.kind === "edit" ? "Uložit" : "Vytvořit"}
        busy={isWriting}
        canSubmit={!formInvalid}
        error={writeError}
        onSubmit={submit}
        onClose={close}
        width="lg"
      >
        {modal.kind === "create" && (
          <Input
            label="Kód sezóny"
            required
            placeholder="2026/2027"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />
        )}
        {/* Draft sets are offered too, and that is the point: registration
            into a season opens months before its rules go active, which is
            exactly when somebody needs to know which ladder applies. The
            sets whose own „Domovská sezóna" is this one come first — the
            label is a hint, never the answer. */}
        <Select
          label="Výchozí sada pravidel"
          value={form.default_rule_set_id}
          onChange={(e) => setForm({ ...form, default_rule_set_id: e.target.value })}
          hint="Podle jejích věkových kategorií se seskupují členové při převodu soupisky do této sezóny. Nevybráno = seskupení podle ročníku narození."
        >
          <option value="">Nevybráno</option>
          {ruleSetOptions.map((r) => (
            <option key={r.id} value={String(r.id)}>
              {r.name}
              {r.status === "draft" ? " (rozpracovaná)" : ""}
              {r.status === "archived" ? " (archivovaná)" : ""}
            </option>
          ))}
        </Select>
        <ResponsiveGrid columns={2}>
          <DatePicker
            label="Začátek registrace"
            value={form.registration_open_from}
            onChange={(v) => setForm({ ...form, registration_open_from: v })}
            hint="Mimo registrační okno mohou soupisky měnit jen správci. Prázdné = registrace bez omezení."
          />
          <DatePicker
            label="Konec registrace"
            value={form.registration_open_to}
            onChange={(v) => setForm({ ...form, registration_open_to: v })}
            minDate={form.registration_open_from ?? undefined}
          />
        </ResponsiveGrid>
        {windowIncomplete && (
          <Alert variant="warn">
            Zadejte začátek i konec registračního okna, nebo obě pole nechte prázdná.
          </Alert>
        )}
        <Textarea
          label="Konfigurace (JSON)"
          rows={6}
          placeholder='{"poplatek_clenstvi_kc": 200}'
          value={form.config}
          onChange={(e) => setForm({ ...form, config: e.target.value })}
          hint="Nepovinný JSON s parametry sezóny (poplatky, limity…). Lze nechat prázdné."
        />
      </FormModal>

    </>
  );
}

// no extra exports needed
